import { FC, useMemo } from 'react';
import { SolarIcon } from '../../components/icons/icons';
import { AnimatedBirdIcon } from '../../components/icons/AnimatedBirdIcon';
import { useGateways } from '../../context/GatewayContext';
import { SolarGatewayData } from '../../components/SolarGatewayData';
import { useNavigate } from '@tanstack/react-router';
import PageContainer from '../../components/layout/PageContainer';

const SolarPage: FC = () => {
  const navigate = useNavigate();
  const { gateways } = useGateways();
  
  // Filter and process solar gateways from all gateways
  const solarGateways = useMemo(() => 
    gateways?.flatMap(gateway => 
      (gateway.ders || [])
        .filter(der => der.type.toLowerCase().includes('solar'))
        .map(der => ({
          ...der,
          gatewayName: gateway.name,
          gatewayId: gateway.id
        }))
    ) || [], [gateways]
  );

  return (
    <PageContainer>
      <div className="mb-6 sm:mb-8 mt-1 sm:mt-1">
        <div className="flex items-center gap-2 mb-2 sm:mb-4">
          <div className="flex items-center gap-2">
            <SolarIcon className="w-5 h-5 text-[#00FF84]" state="active" />
            <h1 className="text-xl font-bold text-white">Solar Data</h1>
          </div>
        </div>
      </div>

      <div className="h-px bg-[#292929] w-full mb-3 -mt-3"></div>

      {/* Connected Solar Gateways List */}
      <div className="space-y-4 sm:space-y-8">
        {solarGateways.length > 0 ? (
          solarGateways
            .sort((a, b) => new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime())
            .map((solar) => {
              const gateway = gateways?.find(g => g.id === solar.gatewayId);
              return (
                <div key={solar.sn} className="space-y-4">
                  <SolarGatewayData
                    gateway={gateway!}
                  />
                </div>
              );
            })
        ) : (
          <div className="bg-[#1D1D1D] rounded-[8px] p-6 border border-[#292929] text-center min-h-[calc(100vh-12rem)] flex flex-col justify-center">
            <AnimatedBirdIcon className="w-12 h-12 mx-auto text-[#00FF84]" />
            <h3 className="mt-4 text-lg font-medium text-gray-300">No Solar DERs Found</h3>
            <p className="mt-2 text-gray-400">No solar DERs are currently connected to any of your gateways.</p>
            {gateways && gateways.length > 0 ? (
              <button 
                onClick={() => navigate({ 
                  to: '/gateway/$gatewayId/devices',
                  params: { gatewayId: gateways[0].id }
                })}
                className="mt-6 inline-block mx-auto px-6 py-3 bg-[#00FF84] hover:bg-[#00CC6A] text-black font-medium rounded-full transition-colors"
              >
                Scan for energy devices
              </button>
            ) : (
              <a 
                href="https://sourceful.energy/zap" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="mt-6 inline-block mx-auto px-6 py-3 bg-[#00FF84] hover:bg-[#00CC6A] text-black font-medium rounded-full transition-colors"
              >
                Get your Sourceful Energy Gateway
              </a>
            )}
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default SolarPage; 